import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom'
import { OneRosterCombinedTeamFlowSyncType, CoreOneRosterConfigurationCombinedTeam, CoreOneRosterConfigurationCombinedTeamSource, OneRosterCombineTeamListing } from "../../../../../../models/CoreOneRosterConfigurationCombinedTeam";
import { RequestState } from "../../../../../../models/RequestState";
import { GetOneRosterCombineTeamActivities, PostOneRosterCombineTeam } from "../../../../../../store/OneRoster/OneRosterCombineTeamAction";
import { FormGroup, Label, Input } from 'reactstrap';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import StatusCode from '../../../../../../util/StatusCode';
import { SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import ModalComponent from "../../../../../misc/ModalComponent";
import { useTranslation } from 'react-i18next';
import SearchableSelectInput from "../../../../../misc/SearchableSelectInput";
import { RootState } from "../../../../../../store/store";

interface OneRosterCombineTeamActivitiesNewProps {
    isOpen: boolean
    toggleModal: (isOpen: boolean) => void
    success: (success: boolean) => void
    disabled: boolean
}


const validation = (team: CoreOneRosterConfigurationCombinedTeam) => {
    let valid: string[] = [];
    if (team.displayName === "") {
        valid.push('displayName')
    }
    if (team.courseTitle === "") {
        valid.push('courseTitle')
    }
    if (team.flowSyncActivityId === "") {
        valid.push('flowSyncActivityId')
    }
    if (!team.sources.length) {
        valid.push('sources')
    }
    return valid;
}

const OneRosterCombineTeamActivitiesNew: React.FC<OneRosterCombineTeamActivitiesNewProps> = (props) => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const [selectedActivities, setSelectedActvities] = useState<string[] | undefined>();
    const activities = useSelector<RootState, RequestState<OneRosterCombineTeamListing[]>>(state => state.onerosterCombineTeam.activities);
    const [team, setTeam] = useState<CoreOneRosterConfigurationCombinedTeam>({
        flowSyncType: OneRosterCombinedTeamFlowSyncType.Activity,
        displayName: "",
        courseTitle: "",
        flowSyncActivityId: "",
        sources: [],
    });
    const [status, setStatus] = useState<StatusCode>(StatusCode.NONE);
    const { t } = useTranslation(['configuration', 'common']);

    const valid = validation(team);

    useEffect(() => {
        if (moduleId) {
            GetOneRosterCombineTeamActivities(moduleId)(dispatch);
        }
    }, [dispatch, moduleId]);

    const postCombinedTeam = () => {
        if (!valid.length && moduleId) {
            setStatus(StatusCode.PENDING);
            PostOneRosterCombineTeam(moduleId, team)
                .then(x => {
                    setStatus(StatusCode.COMPLETE)
                    props.success(true)
                })
                .catch(error => {
                    setStatus(StatusCode.ERROR)
                })
        }
    }

    const updateActivity = (name: string, value: string[] | null | undefined) => {
        if (value === null) {
            setSelectedActvities(undefined);
            return;
        }
        setSelectedActvities(value)

        if (value !== undefined) {
            let sources: CoreOneRosterConfigurationCombinedTeamSource[] = [];
            value.map(x => {
                sources.push({ "flowSyncActivityId": x })
            })
            setTeam({ ...team, "sources": sources });
        }
    }

    const updateTeam = (name: string, value: undefined | string | null) => {
        setTeam({ ...team, [name]: value });
    }

    const mapActivities = () => {
        let activityOptions: SearchableOptionItem<string>[] = [];
        if (activities.code === StatusCode.COMPLETE) {
            activityOptions = activities.data
                .filter(x => x.id !== team.flowSyncActivityId)
                .map(x => ({ value: x.id, label: `${x.title}, ${x.schoolUnitTitle}`}))
        }
        return activityOptions;
    };

    return (<>
        <ModalComponent
            isOpen={props.isOpen}
            toggleModal={() => props.toggleModal(!props.isOpen)}
            header={t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}
            update={postCombinedTeam}
            disabled={valid.length > 0 || props.disabled || status === StatusCode.PENDING || status === StatusCode.COMPLETE}
            confirm={t('action.ok', { ns: 'common' })}
            cancel={t('action.cancel', { ns: 'common' })}
            saveStatus={status}
        >

            <FormGroup>
                <Label for="displayName">{t('oneRosterConfiguration.combineTeam.table.displayName', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    autoComplete="off"
                    value={team.displayName}
                    onChange={(e) => updateTeam("displayName", e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="courseTitle">{t('oneRosterConfiguration.combineTeam.table.courseTitle', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="courseTitle"
                    id="courseTitle"
                    autoComplete="off"
                    value={team.courseTitle}
                    onChange={(e) => updateTeam("courseTitle", e.target.value)}
                />
            </FormGroup>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.owner', { ns: 'configuration' })}</Label>
                <SearchableSelectInput
                    items={activities.code === StatusCode.COMPLETE ? activities?.data.map(a => ({ value: a.id, label: `${a.title}, ${a.schoolUnitTitle}` })) : []}
                    onChange={(name, value) => updateTeam(name, value)}
                    name="flowSyncActivityId"
                    selected={team.flowSyncActivityId}
                    defaultText={t('oneRosterConfiguration.combineTeam.select.owner', { ns: 'configuration' })}
                    disabled={activities.code !== StatusCode.COMPLETE}
                />
            </FormGroup>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.sources', { ns: 'configuration' })}</Label>
                <MultiSelectInput
                    items={mapActivities()}
                    onChange={(name, value) => updateActivity(name, value)}
                    name='sources'
                    selected={selectedActivities}
                    defaultText={t('oneRosterConfiguration.combineTeam.select.sources', { ns: 'configuration' })}
                    disabled={activities.code !== StatusCode.COMPLETE || team?.flowSyncActivityId === ""}
                />
            </FormGroup>
        </ModalComponent>
    </>)
}

export default OneRosterCombineTeamActivitiesNew;
